import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["buttonType", "portalId", "loginStatus", "hubletOverride", "onSuccess", "onFailure"];
import env from 'enviro';
import { buildUrl } from 'hub-http/helpers/url';
import { stringify } from 'hub-http/helpers/params';
import { useEffect, useRef, useState } from 'react';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UISelectableButton from 'UIComponents/button/UISelectableButton';
import { fetchAuthConfigForDataSync as buildAuthConfigFetcherForDataSync } from './DataSync';
import { AuthenticateError, DuplicateDataSyncAccountError, MissingPermissions } from './LowLevelUnifiedAuth';
import { getFullUrl } from 'hubspot-url-utils';
import { buildApiPayload } from './UniversalAuthSteps';
import { jsx as _jsx } from "react/jsx-runtime";
const googleAppId = env.isQa() ? 1203680 : 232794;
const oAuthRedirectOrigin = `https://oauth-redirect.hubspot${env.isQa() ? 'qa' : ''}.com`;
export function GoogleDataSyncAuthButton(_ref) {
  let {
      buttonType = 'loading',
      portalId,
      loginStatus = 'completed',
      hubletOverride,
      onSuccess,
      onFailure
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const oauthWindowRef = useRef(undefined);
  const oauthStateRef = useRef('');
  const [status, setStatus] = useState('idle');
  useEffect(() => {
    if (status === 'authorizing' && loginStatus === 'completed') {
      let timer;
      let heartbeat;
      let messageHandler;
      const fetchAuthConfig = buildAuthConfigFetcherForDataSync(portalId, googleAppId, undefined, hubletOverride);
      fetchAuthConfig().then(authConfig => {
        timer = setTimeout(() => {
          const oauthWindow = oauthWindowRef.current;
          if (oauthWindow) {
            oauthWindow.close();
            setStatus('failed');
            onFailure({
              type: 'OAuthWindowClosed'
            });
          }
        }, 300000);
        heartbeat = setInterval(() => {
          const oauthWindow = oauthWindowRef.current;
          if (!oauthWindow || oauthWindow && oauthWindow.closed) {
            setStatus('failed');
            onFailure({
              type: 'OAuthWindowClosed'
            });
          }
        }, 1000);
        messageHandler = event => {
          const oauthWindow = oauthWindowRef.current;
          if (event.data && oauthWindow && event.source === oauthWindow) {
            if ((event.origin === oAuthRedirectOrigin || event.origin === getFullUrl('app')) && event.data === 'ping') {
              oauthWindow.postMessage('pong', event.origin);
            } else if (event.origin === oAuthRedirectOrigin && event.data.type === 'AUTH_RESPONSE') {
              const oauthRedirectSearchParams = new URLSearchParams(event.data.queryString);

              // CSRF protection: https://datatracker.ietf.org/doc/html/rfc6749#section-10.12
              // Verify the validity of the request by matching the received state with the user-agent's authenticated state.
              // We have 3 cases:
              //  1. There’s a state parameter and it matches the user-agent’s authenticated state
              //  2. The state does not match, the request is invalid and should be rejected
              //  3. There is no state, the request is invalid and should be rejected

              if (oauthRedirectSearchParams.has('state')) {
                // Case 1. There’s a state parameter and it matches the user-agent’s authenticated state
                if (oauthStateRef.current === oauthRedirectSearchParams.get('state')) {
                  const payload = buildApiPayload(authConfig.authFlow.sessionId, authConfig.authFlow.steps, {}, oauthRedirectSearchParams, oauthStateRef.current, authConfig.oAuthScope);
                  authConfig.finishAuthFlow(payload).then(account => {
                    onSuccess(account.id);
                    setStatus('success');
                  }).catch(err => {
                    setStatus('failed');
                    if (err instanceof AuthenticateError || err instanceof DuplicateDataSyncAccountError || err instanceof MissingPermissions) {
                      onFailure(err.toAuthError());
                    } else if (err.responseJSON && err.responseJSON.message) {
                      onFailure({
                        type: 'AuthenticateError',
                        message: err.responseJSON.message
                      });
                    } else {
                      onFailure({
                        type: 'AuthenticateError',
                        message: err.message
                      });
                    }
                  });
                } else {
                  // Case 2: The state does not match, the request is invalid and should be rejected
                  setStatus('failed');
                  onFailure({
                    type: 'OAuthWindowClosed'
                  });
                }
              } else {
                // Case 3: There is no state, the request is invalid and should be rejected
                setStatus('failed');
                onFailure({
                  type: 'OAuthWindowClosed'
                });
              }

              // TODO: we should send case 2 & 3 to the UnifiedAuth BE in the future so we can track them and present a nice error message.
              clearTimeout(timer);
              clearInterval(heartbeat);
              oauthWindow.close();
              oauthStateRef.current = '';
            }
          }
        };
        window.addEventListener('message', messageHandler);
        const oAuthState = authConfig.authFlow.sessionId;
        oauthStateRef.current = oAuthState;
        const authorizeUrl = buildUrl(Object.assign({}, authConfig.makeAuthorizeUrlDescriptor(), {
          query: stringify({
            sessionId: authConfig.authFlow.sessionId,
            scope: authConfig.oAuthScope,
            state: oAuthState,
            portalId
          })
        }));
        const oauthWindow = oauthWindowRef.current;
        if (oauthWindow && !oauthWindow.closed) {
          oauthWindow.location = authorizeUrl;
        } else {
          setStatus('failed');
          onFailure({
            type: 'OAuthWindowClosed'
          });
        }
      }).catch(() => {
        onFailure({
          type: 'AuthenticateError'
        });
        setStatus('failed');
        const oauthWindow = oauthWindowRef.current;
        if (oauthWindow) oauthWindow.close();
      });
      return () => {
        if (timer) clearTimeout(timer);
        if (heartbeat) clearInterval(heartbeat);
        if (messageHandler) window.removeEventListener('message', messageHandler);
      };
    }
    if (loginStatus === 'failed') {
      const oauthWindow = oauthWindowRef.current;
      if (oauthWindow) {
        oauthWindow.close();
        onFailure({
          type: 'OAuthWindowClosed'
        });
      }
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, loginStatus]);
  const onClick = event => {
    const width = 620;
    const height = 790;
    const {
      screenY,
      screenX,
      outerHeight,
      outerWidth
    } = window;

    // calculate top and left in order to center the window on the users' screen
    const top = Math.round(screenY + Math.max(0, Math.floor(outerHeight - height) / 2));
    const left = Math.round(screenX + Math.max(0, Math.floor(outerWidth - width) / 2));
    const oAuthWindow = window.open('about:blank', '_blank', `menubar=no,location=no,resizable=yes,scrollbars=yes,status=no,titlebar=no,toolbar=no,width=${width},height=${height},top=${top},left=${left}`);
    if (oAuthWindow) {
      oauthWindowRef.current = oAuthWindow;
      setStatus('authorizing');
      if (props.onClick) {
        props.onClick(event);
      }
    } else {
      setStatus('failed');
      onFailure({
        type: 'OAuthWindowBlocked'
      });
    }
  };
  if (buttonType === 'loading') {
    return /*#__PURE__*/_jsx(UILoadingButton, Object.assign({}, props, {
      loading: status === 'authorizing',
      failed: status === 'failed',
      onClick: onClick,
      children: props.children
    }));
  }
  return /*#__PURE__*/_jsx(UISelectableButton, Object.assign({}, props, {
    onClick: onClick,
    children: props.children
  }));
}